import type { ScopeRef } from "@/components/scope-picker/types";
import { getCurrentUser } from "@/lib/auth-api";
import { decodeScope } from "@/lib/scope";

export const DEFAULT_DASHBOARD_SCOPE: ScopeRef = { type: "wlasne" };

/** Klucz localStorage zapisanego scope — per użytkownik. */
export function dashboardScopePersistKey(userId?: number | string | null): string {
	return userId ? `raporty-dashboard:scope:${userId}` : "raporty-dashboard:scope:anon";
}

/**
 * Id użytkownika znane SYNCHRONICZNIE, już w loaderze: `_currentUser` w
 * auth-api ustawia `loadUser()` z `beforeLoad`, więc jest gotowe przed
 * pierwszym renderem. `useAuth().user` to stan Provider-a i na pierwszym
 * renderze bywa `null` — wtedy strona brała klucz `:anon`, czytała pusty
 * localStorage, strzelała zapytania na `wlasne`, a po dojściu `user.id`
 * przeliczała je jeszcze raz na zapisanym scope (trzecia fala requestów).
 */
export function currentDashboardUserId(): number | string | null {
	return getCurrentUser()?.id ?? null;
}

export function readPersistedDashboardScope(userId?: number | string | null): ScopeRef {
	try {
		const raw = window.localStorage.getItem(dashboardScopePersistKey(userId));
		return (raw ? decodeScope(raw) : null) ?? DEFAULT_DASHBOARD_SCOPE;
	} catch {
		return DEFAULT_DASHBOARD_SCOPE;
	}
}

/**
 * Efektywny scope dashboardu: `?scope=` z URL-a wygrywa nad zapisanym, brak
 * obu = `wlasne`. JEDNO źródło prawdy dla loadera i strony — inaczej loader
 * pyta bez scope, hooki z `wlasne`, backend rozstrzyga oba tak samo, a query
 * keys się różnią i każdy widget leci dwa razy.
 */
export function resolveDashboardScope(
	urlScope: string | undefined,
	persistedScope: ScopeRef,
): ScopeRef {
	return (urlScope ? decodeScope(urlScope) : null) ?? persistedScope;
}

/**
 * Etykieta zakresu aptek do czytania: na kartach, w kontekście szczegółu i w
 * arkuszu Kryteria. Token `wlasne` obejmuje CAŁĄ sieć aptek własnych, czego
 * samo słowo nie mówi. Wartość wysyłana do API, query key i localStorage
 * zostają surowym tokenem — to zmiana wyłącznie prezentacyjna.
 */
export function scopeDisplayLabel(scope: string | null | undefined): string {
	if (!scope || scope === "wlasne") return "Cała sieć (własne)";
	if (scope.startsWith("kr:")) return `KR: ${scope.slice(3)}`;
	return scope;
}
